import type { MemberRole, ScanState, TeamAdmin } from "@boem/core/shared";
import { ApiError, call } from "./api.ts";
import { apiBase } from "./org.ts";

// Screenshots/recordings and the team admin screen (phase 2).

export interface MediaConfig {
  image: { maxBytes: number; types: string[] };
  video: { maxBytes: number; maxSeconds: number; types: string[] };
  /** storage can presign: files go straight to it (STORAGE=s3), not through the server */
  direct?: boolean;
}

export interface UploadResult {
  id: string;
  kind: "image" | "video";
  scan: ScanState;
  /** the same file was already on the item */
  duplicate: boolean;
}

let config: Promise<MediaConfig> | null = null;
/** Upload limits from the server (fetched once). */
export function mediaConfig(): Promise<MediaConfig> {
  config ??= call<MediaConfig>("GET", "/api/media/config").catch((err) => {
    config = null;
    throw err;
  });
  return config;
}

/** Where a file goes: an item (#num on the current board), or one of your notes on it. */
export interface UploadTarget {
  num: number;
  noteId?: string | null;
}

/**
 * Upload one file to an item or a note, with progress. Every upload dialog goes through here: the
 * card's Add screenshot, the Notes dialog, and the New item dialog (once the item exists). When the
 * board's storage can presign (STORAGE=s3) the file goes straight to it, so a big phone video never
 * passes through the board's server or its proxy; otherwise through the server (multipart).
 */
export async function uploadFile(target: UploadTarget, file: File, onProgress: (sent: number, total: number) => void, signal?: AbortSignal): Promise<UploadResult> {
  const cfg = await mediaConfig().catch(() => null);
  if (cfg?.direct) {
    try {
      return await directUpload(target, file, onProgress, signal);
    } catch (err) {
      // storage unreachable from the browser (a missing bucket CORS rule, a blocked host): through the server instead
      if (!(err instanceof DirectUnreachable)) throw err;
      console.warn("direct upload failed; uploading through the server instead", err.message);
    }
  }
  return uploadAttachment(target.num, file, onProgress, signal, target.noteId);
}

/** What the server answers when a direct upload starts: where and how to PUT the file. */
export interface UploadTicket {
  id: string;
  method: "PUT";
  url: string;
  headers: Record<string, string>;
  expiresAt: string;
  bytes: number;
  storage: "s3" | "local";
}

/** The PUT never reached storage (status 0): a CORS or network problem, not a refusal. */
class DirectUnreachable extends Error {}

const TYPE_BY_EXT: Record<string, string> = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif", mp4: "video/mp4", webm: "video/webm", mov: "video/quicktime" };

/** The type to declare: the browser's, or from the name when it has none (the server checks the bytes). */
export const declaredType = (f: Pick<File, "type" | "name">) => f.type || TYPE_BY_EXT[f.name.split(".").pop()?.toLowerCase() ?? ""] || "application/octet-stream";

/** Start, PUT straight to storage (with progress), finish. */
async function directUpload(target: UploadTarget, file: File, onProgress: (sent: number, total: number) => void, signal?: AbortSignal): Promise<UploadResult> {
  const t = await call<UploadTicket>("POST", `${apiBase()}/uploads`, { num: target.num, noteId: target.noteId ?? null, name: file.name || "pasted-image.png", mime: declaredType(file), bytes: file.size });
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(t.method, t.url);
    for (const [k, v] of Object.entries(t.headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(xhr.status, "upload_failed", `Storage refused the file (${xhr.status}). Try again.`)));
    xhr.onerror = () => reject(new DirectUnreachable(`PUT to storage failed for ${t.id}`));
    xhr.onabort = () => reject(new ApiError(0, "aborted", "Upload cancelled."));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
  const done = await call<{ id: string; kind: "image" | "video"; scan: ScanState }>("POST", `${apiBase()}/uploads/${encodeURIComponent(t.id)}/finish`, {});
  return { id: done.id, kind: done.kind, scan: done.scan, duplicate: false };
}

/**
 * Upload one file through the server (multipart), with progress (fetch can't report upload
 * progress, XHR can). The server checks the type from the file's bytes and has the final say on size.
 */
export function uploadAttachment(num: number, file: File, onProgress: (sent: number, total: number) => void, signal?: AbortSignal, noteId?: string | null): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // with a note: added to that note of yours and shown under it
    xhr.open("POST", `${apiBase()}/items/${num}/attachments${noteId ? `?note=${encodeURIComponent(noteId)}` : ""}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      let data: { error?: string; message?: string } & Partial<UploadResult> = {};
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        /* not JSON - e.g. a proxy error page */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as UploadResult);
      else
        reject(
          new ApiError(
            xhr.status,
            data.error ?? "error",
            data.message ?? (xhr.status === 413 ? "That file is too large." : `The upload failed (${xhr.status}).`),
          ),
        );
    };
    xhr.onerror = () => reject(new ApiError(0, "network", "The upload was interrupted. Check your connection and try again."));
    xhr.onabort = () => reject(new ApiError(0, "aborted", "Upload cancelled."));
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    const form = new FormData();
    form.append("file", file, file.name || "pasted-image.png");
    xhr.send(form);
  });
}

export const mediaApi = {
  remove: (num: number, id: string) => call("DELETE", `${apiBase()}/items/${num}/attachments/${id}`),
  /** admins: show a hidden file to everyone after checking it */
  release: (num: number, id: string) => call("POST", `${apiBase()}/items/${num}/attachments/${id}/release`, {}),
};

export interface NewMemberInput {
  name: string;
  email: string | null;
  role: MemberRole;
  canBuild: boolean;
  canApprove: boolean;
  isVerifier: boolean;
  isAdmin: boolean;
}

export type MemberPatch = Partial<Pick<NewMemberInput, "email" | "role" | "canBuild" | "canApprove" | "isVerifier" | "isAdmin">>;

export interface MediaStatus {
  counts: Partial<Record<ScanState, number>>;
  hidden: { id: string; itemNum: number; kind: string; labels: string[]; scannedAt: string | null }[];
}

export const adminApi = {
  team: () => call<TeamAdmin>("GET", "/api/admin/team"),
  add: (m: NewMemberInput) => call<{ id: string }>("POST", "/api/admin/members", m),
  update: (id: string, patch: MemberPatch) => call("PATCH", `/api/admin/members/${id}`, patch),
  setActive: (id: string, active: boolean) => call<{ openItems: number }>("POST", `/api/admin/members/${id}/active`, { active }),
  media: () => call<MediaStatus>("GET", "/api/admin/media"),
  scanBacklog: () => call<{ queued: number }>("POST", "/api/admin/media/scan", {}),
};
